import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { AgedPayablesSummary, combineAgedPayables } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one or more Aged Payables Excel files (.xlsx).',
  'Click Process & Combine to run all the checks.',
  'The combined workbook (one sheet per file) downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

/** The log lines use **bold** markers; render them as <strong>. */
function LogLine({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <li>
      {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
    </li>
  );
}

function AgedPayablesPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<AgedPayablesSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload at least one Aged Payables file.');
      return;
    }
    await run(() => combineAgedPayables(files), 'Combining failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Checks applied</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>One sheet per file, named from the filename.</li>
          <li>Duplicate invoice references highlighted in red (column D).</li>
          <li>Direct Debit section rows marked in red font.</li>
          <li>Contacts with Associates populated highlighted in red.</li>
          <li>"Associate must be assigned" flagged in Labs / Implant / Invisalign sections.</li>
        </ul>
      </div>

      <FileDrop
        label="Aged Payables files"
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Combining…' : 'Process & Combine'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Combining…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <ul className="mt-2 list-none space-y-1 text-xs text-green-800">
              {result.summary.log.map((line, i) => (
                <LogLine key={i} text={line} />
              ))}
            </ul>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload one or more .xlsx files above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default AgedPayablesPanel;
